import { useEffect, useState } from 'react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';

import { CAN_USE_DOM } from './utils/canUseDOM';
import { useToolbarDisabler } from './hooks/useToolbarDisabler';
import { useSharedHistoryContext } from './context/SharedHistoryContext';
import ActionsPlugin from './plugins/ActionsPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin';
import CodeActionMenuPlugin from './plugins/CodeActionMenuPlugin';
import ComponentPickerPlugin from './plugins/ComponentPickerPlugin';
import CustomOnChangePlugin from './plugins/CustomOnChangePlugin';
import DragDropPaste from './plugins/DragDropPastePlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import ImagesPlugin from './plugins/ImagesPlugin';
// Disabling Layout Columns for v1 of Rich Text Editor Implementation
// import { LayoutPlugin } from './plugins/LayoutPlugin/LayoutPlugin';
import LinkPlugin from './plugins/LinkPlugin';
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin';
import MarkdownShortcutPlugin from './plugins/MarkdownShortcutPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin';
import TableActionMenuPlugin from './plugins/TableActionMenuPlugin';
import TableCellResizer from './plugins/TableCellResizer';
import TableHeaderPlugin from './plugins/TableHeaderPlugin';
import TableHoverActionsPlugin from './plugins/TableHoverActionsPlugin';
import TableOfContentsPlugin from './plugins/TableOfContentsPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import AdmonitionPlugin from './plugins/AdmonitionPlugin';
import ContentEditable from './ui/ContentEditable';

import './index.css';
import { useSettings } from './context/SettingsContext';
import { Config } from './types';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

interface EditorUIProps {
  value: string;
  onChange: (value: string) => void;
  editable?: boolean;
  handleEditingRichText: (isEditingRichText: boolean) => void;
  isEditingRichText?: boolean;
  widgetConfig: Config;
  siteId: string;
}

export default function EditorUI({
  value,
  onChange,
  editable = true,
  handleEditingRichText,
  isEditingRichText = true,
  siteId,
  widgetConfig,
}: EditorUIProps): JSX.Element {
  const { enableTableOfContents, yLocation } = widgetConfig;
  const { historyState } = useSharedHistoryContext();
  const {
    settings: {
      hasLinkAttributes,
      isRichText,
      shouldPreserveNewLinesInMarkdown,
      tableCellMerge,
      tableCellBackgroundColor,
    },
  } = useSettings();
  const isEditable = useLexicalEditable();
  const [editor] = useLexicalComposerContext();
  const [
    floatingAnchorElem,
    setFloatingAnchorElem,
  ] = useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState<boolean>(
    false
  );
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
  const [
    floatingTextFormatToolbarDisabled,
    setFloatingTextFormatToolbarDisabled,
  ] = useState(false);

  useToolbarDisabler(editor, setFloatingTextFormatToolbarDisabled);

  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const placeholder = isEditingRichText
    ? 'Enter text...'
    : 'Click the edit icon below to enter text...';

  if (!widgetConfig || typeof widgetConfig !== 'object') {
    return <></>;
  }

  return (
    <div>
      {isEditingRichText && editable && (
        <span data-test-id="rich-text-toolbar">
          <ToolbarPlugin
            setIsLinkEditMode={setIsLinkEditMode}
            yLocation={yLocation}
          />
        </span>
      )}
      <div
        className="editor-container"
        style={{
          position: editable ? 'relative' : 'fixed',
          width:
            typeof widgetConfig?.width === 'number' ? widgetConfig.width : 500,
          height:
            typeof widgetConfig?.height === 'number'
              ? `${widgetConfig.height}px`
              : widgetConfig?.height ?? 300,
        }}
      >
        <DragDropPaste siteId={siteId} isRichText={isRichText} />
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        <ComponentPickerPlugin />
        {enableTableOfContents && isRichText && widgetConfig.visible && (
          <TableOfContentsPlugin
            data-test-id={'table-of-contents'}
            widgetConfig={widgetConfig}
          />
        )}
        <>
          <HistoryPlugin externalHistoryState={historyState} />
          <RichTextPlugin
            contentEditable={
              <div
                className="editor-scroller"
                style={{ resize: editable ? 'vertical' : 'none' }}
              >
                <div className="editor" ref={onRef}>
                  <ContentEditable
                    className={isEditable ? 'is-editing' : ''}
                    placeholder={placeholder}
                    widgetConfig={widgetConfig}
                    previewScrollbar={!editable}
                  />
                </div>
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <MarkdownShortcutPlugin />
          <CodeHighlightPlugin />
          <ListPlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <TablePlugin
            hasCellMerge={tableCellMerge}
            hasCellBackgroundColor={tableCellBackgroundColor}
          />
          <TableHeaderPlugin />
          <TableCellResizer />
          <ImagesPlugin />
          <LinkPlugin hasLinkAttributes={hasLinkAttributes} />
          <ClickableLinkPlugin disabled={isEditable} newTab={false} />
          <HorizontalRulePlugin />
          <TabFocusPlugin />
          <TabIndentationPlugin />
          {/* Todo: Disabling Layout Columns for v1 of Rich Text Editor Implementation*/}
          {/*<LayoutPlugin />*/}
          <AdmonitionPlugin />
          {/*CustomOnChangePlugin serializes the Lexical editor state to an HTML string.*/}
          <CustomOnChangePlugin value={value} onChange={onChange} />
          {floatingAnchorElem && !isSmallWidthViewport && (
            <>
              <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
              <CodeActionMenuPlugin
                anchorElem={floatingAnchorElem}
                editable={editable}
              />
              <FloatingLinkEditorPlugin
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
              <TableActionMenuPlugin
                anchorElem={floatingAnchorElem}
                cellMerge={true}
              />
              <TableHoverActionsPlugin anchorElem={floatingAnchorElem} />
              {!floatingTextFormatToolbarDisabled && (
                <FloatingTextFormatToolbarPlugin
                  anchorElem={floatingAnchorElem}
                  setIsLinkEditMode={setIsLinkEditMode}
                />
              )}
            </>
          )}
        </>
        {editable && (
          <ActionsPlugin
            isRichText={isRichText}
            shouldPreserveNewLinesInMarkdown={shouldPreserveNewLinesInMarkdown}
            handleEditingRichText={handleEditingRichText}
            isEditingRichText={isEditingRichText}
          />
        )}
      </div>
    </div>
  );
}
